interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 1000; // Maximum number of cache entries
  private readonly MAX_MEMORY_USAGE = 50 * 1024 * 1024; // 50MB max memory usage

  /**
   * Set a value in the cache with production-ready safeguards
   */
  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    try {
      // Check cache size limits
      if (this.cache.size >= this.MAX_CACHE_SIZE) {
        this.evictOldestEntries();
      }

      // Check memory usage
      const estimatedSize = this.estimateItemSize(key, data);
      if (this.estimateMemoryUsage() + estimatedSize > this.MAX_MEMORY_USAGE) {
        this.evictOldestEntries();
      }

      this.cache.set(key, {
        data,
        timestamp: Date.now(),
        ttl
      });
    } catch (error) {
      console.warn('Cache set operation failed:', error);
      // Don't throw error to prevent app crashes
    }
  }

  /**
   * Get a value from the cache with error handling
   */
  get<T>(key: string): T | null {
    try {
      const item = this.cache.get(key);
      
      if (!item) {
        return null;
      }

      // Check if item has expired
      if (Date.now() - item.timestamp > item.ttl) {
        this.cache.delete(key);
        return null;
      }

      return item.data as T;
    } catch (error) {
      console.warn('Cache get operation failed:', error);
      return null;
    }
  }

  /**
   * Check if a key exists in cache and is not expired
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    
    if (!item) {
      return false;
    }

    // Check if item has expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Delete a specific key from cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    keys: string[];
    memoryUsage: number;
  } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Estimate memory usage of cache
   */
  private estimateMemoryUsage(): number {
    let totalSize = 0;
    for (const [key, item] of this.cache.entries()) {
      totalSize += key.length * 2; // Unicode characters
      totalSize += JSON.stringify(item).length * 2;
    }
    return totalSize;
  }

  /**
   * Estimate size of a single cache item
   */
  private estimateItemSize<T>(key: string, data: T): number {
    return key.length * 2 + JSON.stringify(data).length * 2;
  }

  /**
   * Evict oldest cache entries when limits are reached
   */
  private evictOldestEntries(): void {
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    // Remove oldest 20% of entries
    const toRemove = Math.ceil(entries.length * 0.2);
    for (let i = 0; i < toRemove; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  /**
   * Generate cache key for API requests
   */
  generateApiKey(endpoint: string, params?: Record<string, any>): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `api:${endpoint}:${paramString}`;
  }

  /**
   * Generate cache key for user-specific data
   */
  generateUserKey(userId: string, dataType: string, params?: Record<string, any>): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `user:${userId}:${dataType}:${paramString}`;
  }
}

// Create singleton instance
export const cacheService = new CacheService();

// Auto-cleanup expired entries every 5 minutes
setInterval(() => {
  cacheService.clearExpired();
}, 5 * 60 * 1000);

export default cacheService;
